import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { RouteRoles } from "@/components/RouteRoles";
import MainLayout from "@/components/layouts/MainLayout";
import { useAuth } from "@/hooks/useAuth";

// Auth pages
import Login from "@/pages/auth/Login";
import Retrieve from "@/pages/auth/Retrieve";
import Update from "@/pages/auth/Update";

// Main pages
import Dashboard from "@/pages/dashboard/Dashboard";

// Users
import UsersList from "@/pages/users/UsersList";
import Profile from "@/pages/users/Profile";

// Companies
import CompaniesList from "@/pages/companies/CompaniesList";
import CompaniesForm from "@/pages/companies/CompaniesForm";
import CategoriesForm from "@/pages/companies/CategoriesForm";

// Facilities
import FacilitiesList from "@/pages/facilities/FacilitiesList";
import { FacilitiesDetails } from "@/pages/facilities/FacilitiesDetails";

// Vehicles
import { VehiclesList } from "@/pages/vehicles/VehiclesList";
import { VehiclesConsumption } from "@/pages/vehicles/VehiclesComsumption";

// Coefficients
import { CoefficientsMain } from "@/pages/coefficients/CoefficentsMain";
import { CombustionFactorList } from "@/pages/coefficients/combustion/CombustionFactorList";
import EmissionFactorsList from "@/pages/coefficients/emission/EmissionFactorsList";
import { ConsumptionFactorList } from "@/pages/coefficients/consumption/ConsumptionFactorList";
import { ElectricityFactorList } from "@/pages/coefficients/electricity/ElectricityFactorList";

// Settings
import { SettingsMain } from "@/pages/settings/SettingsMain";
import { SuppliersList } from "@/pages/settings/suppliers/SuppliersList";
import { FuelTypesList } from "@/pages/settings/fuel-types/FuelTypesList";
import { VehicleTypesList } from "@/pages/settings/vehicle-types/VehicleTypesList";
import { CategoriesList } from "@/pages/settings/categories/CategoriesList";
import MeasureHome from "./pages/measure/MeasureHome";
import CommunicationHome from "./pages/communication/CommunicationHome";
import { Base } from "./pages/communication/components/Base";

export default function AppRoutes() {
  const { user } = useAuth();
  const defaultRoute = user?.rol === "admin" ? "/dashboard" : "/dashboard";

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/retrieve" element={<Retrieve />} />
      <Route path="/recupera/:token" element={<Update />} />

      {/* Base protected route - requires authentication */}
      <Route element={<ProtectedRoute />}>
        {/* Common layout for all authenticated routes */}
        <Route path="report/estandard" element={<Base />} />
        <Route element={<MainLayout />}>
          <Route index element={<Navigate to={defaultRoute} replace />} />

          {/* Public authenticated routes */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />
          <Route path="my-company" element={<CompaniesForm />} />
          <Route path="my-categories" element={<CategoriesForm />} />
          <Route path="measure" element={<MeasureHome />} />
          <Route path="communication" element={<CommunicationHome />} />

          {/* Admin-only routes */}
          <Route
            path="users"
            element={
              <RouteRoles allowedRoles={["admin", "client"]}>
                <UsersList />
              </RouteRoles>
            }
          />

          {/* Admin and client routes */}
          <Route
            path="companies"
            element={
              <RouteRoles allowedRoles={["admin", "client"]}>
                <CompaniesList />
              </RouteRoles>
            }
          />

          {/* Settings - Admin only */}
          <Route
            path="settings"
            element={
              <RouteRoles allowedRoles={["admin"]}>
                <SettingsMain />
              </RouteRoles>
            }
          >
            <Route path="categories" element={<CategoriesList />} />
            <Route path="comercializadoras" element={<SuppliersList />} />
            <Route path="fuel-types" element={<FuelTypesList />} />
            <Route path="vehicle-types" element={<VehicleTypesList />} />
          </Route>

          {/* Facilities - All roles */}
          <Route path="facilities">
            <Route index element={<FacilitiesList />} />
            <Route path=":id" element={<FacilitiesDetails />} />
          </Route>

          {/* Coefficients - Admin only */}
          <Route
            path="coefficients"
            element={
              <RouteRoles allowedRoles={["admin"]}>
                <CoefficientsMain />
              </RouteRoles>
            }
          >
            <Route path="combustion" element={<CombustionFactorList />} />
            <Route path="emission" element={<EmissionFactorsList />} />
            <Route path="consumption" element={<ConsumptionFactorList />} />
            <Route path="electricity" element={<ElectricityFactorList />} />
          </Route>

          {/* Vehicles - All roles */}
          <Route path="vehicles">
            <Route index element={<VehiclesList />} />
            <Route path=":id" element={<VehiclesConsumption />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={defaultRoute} replace />} />
    </Routes>
  );
}
